import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box} from 'views/components';
import {translate} from '../Translate';
import {TaskEditor} from '../task';

import backend from 'utils/backend';

import CommandResponseModal from './CommandResponseModal';
import CommandResponseView from './CommandResponseView';
import SlashCommandTextbox from './SlashCommandTextbox';

const Commands = (props) => {
  const {
    autoFocus,
    caregivers = [],
    clients = [],
    communicationTarget,
    onInputValueChange,
    playbook,
    requester,
    resources,
    statusProgressWrap: progressWrap,
    playbooks,
    skills,
  } = props;

  const requesterRn = requester.userRn;
  const [commandExecutionResponse, setCommandExecutionResponse] = React.useState(null);
  const [lastExecutedSkill, setLastExecutedSkill] = React.useState(null);
  const [isExecutingCommand, setIsExecutingCommand] = React.useState(false);
  const [commandInputValue, setCommandInputValue] = React.useState('');
  const [commandResources, setCommandResources] = React.useState([]);
  const [focusSlashCommand, setFocusSlashCommand] = React.useState(false);

  const getExecutionResources = React.useCallback((additionalResources= []) => {
    const mergedResources = [...resources, ...additionalResources];
    const uniqueResources = [];
    const seenResourceRns = new Set();

    mergedResources.forEach((resource) => {
      if (!resource?.rn || seenResourceRns.has(resource.rn)) {
        return;
      }

      seenResourceRns.add(resource.rn);
      uniqueResources.push(resource);
    });

    return uniqueResources;
  }, [resources]);

  const executeSkill = React.useCallback(async (skill, promptText, additionalResources=commandResources) => {
    let response = null;

    await progressWrap(async () => {
      try {
        setIsExecutingCommand(true);

        const resourcesToSubmit = getExecutionResources(additionalResources).map((resource) => ({
          type: resource.type,
          rn: resource.rn,
        }));

        const requestBody = {
          ressources: [
            ...resourcesToSubmit,
            {type: 'USER', rn: requesterRn},
          ],
          text: promptText || ' ',
        };

        setLastExecutedSkill({
          additionalResources,
          promptText,
          skill,
        });

        response = await backend.skills.executeSkill(skill.skillRn, requestBody);
        setCommandExecutionResponse(response.result);
      } finally {
        setIsExecutingCommand(false);
      }
    }, {
      showProgress: false,
    });

    return response;
  }, [commandResources, getExecutionResources, progressWrap, requesterRn]);

  const handleRegenerate = React.useCallback(() => {
    if (!lastExecutedSkill) {
      return null;
    }

    return executeSkill(lastExecutedSkill.skill, lastExecutedSkill.promptText, lastExecutedSkill.additionalResources);
  }, [executeSkill, lastExecutedSkill]);

  const handleDiscard = React.useCallback(() => {
    setCommandExecutionResponse(null);
  }, []);

  const executePlaybook = React.useCallback(async (selectedPlaybook, additionalResources=commandResources) => {
    const resourceRns = getExecutionResources(additionalResources).map((resource) => resource.rn);

    await progressWrap(async () => {
      try {
        setIsExecutingCommand(true);
        await playbook.execute(selectedPlaybook.playbookRn, resourceRns);
      } finally {
        setIsExecutingCommand(false);
      }
    }, {
      showProgress: false,
    });
  }, [commandResources, getExecutionResources, playbook, progressWrap]);

  const handleTaskSubmit = React.useCallback((taskData) => {
    progressWrap(async () => {
      try {
        await backend.task.addTask(taskData);
      } finally {
        setCommandInputValue('');
      }
    });
  }, [progressWrap]);

  React.useEffect(() => {
    onInputValueChange?.(commandInputValue);
  }, [commandInputValue, onInputValueChange]);

  const isTaskEditorMode = React.useMemo(() => {
    if (!commandInputValue) {
      return false;
    }

    return !commandInputValue.startsWith('/');
  }, [commandInputValue]);

  return (
    <Box>
      {
        commandExecutionResponse && communicationTarget &&
        <CommandResponseView
          communicationTarget={communicationTarget}
          requester={requester}
          response={commandExecutionResponse}
          onDiscard={handleDiscard}
          isRegenerating={isExecutingCommand}
          onRegenerate={handleRegenerate}
        />
      }
      {
        commandExecutionResponse && !communicationTarget &&
        <CommandResponseModal
          clientRn={resources.find((r) => r.type === 'CLIENT')?.rn}
          isRegenerating={isExecutingCommand}
          onClose={handleDiscard}
          onRegenerate={handleRegenerate}
          requester={requester}
          response={commandExecutionResponse}
        />
      }
      {
        isTaskEditorMode ?
          <Box
            sx={{
              my: '12px',
            }}
          >
            <TaskEditor
              autoFocusTitle
              onCancel={() => {
                setFocusSlashCommand(true);
                setCommandInputValue('');
              }}
              onSubmit={handleTaskSubmit}
              onTitleChange={(value) => {
                if (!value) {
                  setFocusSlashCommand(true);
                  setCommandInputValue('');
                }
              }}
              task={{
                title: commandInputValue,
                connectionInfo: [
                  {
                    connectionRn: resources[0].rn,
                    firstName: resources[0].firstName,
                    lastName: resources[0].lastName || '',
                  },
                ],
              }}
            />
          </Box> :
          <SlashCommandTextbox
            autoFocus={autoFocus || focusSlashCommand}
            caregivers={caregivers}
            clients={clients}
            isExecutingCommand={isExecutingCommand}
            onExecutePlaybook={executePlaybook}
            onMentionedResourcesChange={setCommandResources}
            onExecuteSkill={executeSkill}
            placeholder={translate('playbooks.List.ExecutePlaceholder')}
            playbooks={playbooks}
            onInputValueChange={setCommandInputValue}
            skills={skills}
          />
      }
    </Box>
  );
};

Commands.propTypes = {
  autoFocus: PropTypes.bool,
  caregivers: PropTypes.array,
  clients: PropTypes.array,
  communicationTarget: PropTypes.object,
  onInputValueChange: PropTypes.func,
  playbook: PropTypes.shape({
    execute: PropTypes.func,
  }).isRequired,
  requester: PropTypes.object.isRequired,
  resources: PropTypes.arrayOf(PropTypes.shape({
    rn: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  })).isRequired,
  playbooks: PropTypes.array.isRequired,
  skills: PropTypes.array.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  requester: state.user,
  playbooks: state.playbooks.allPlaybooks,
  skills: state.skills.allSkills,
});

export default connect(mapStateToProps, dispatch)(React.memo(Commands));
